import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const solutionItems = [
    { label: 'Financial Data Intelligence', path: '/solutions/financial-data-intelligence', external: false },
    { label: 'Ops Intelligence Suite', path: '/solutions/ops-intelligence-suite', external: false },
    { label: 'Agentic Co-workers', path: '/solutions/agentic-coworkers', external: false },
    { label: 'Financial Intelligence Assistant', path: '/solutions/financial-intelligence-assistant', external: false },
    { label: 'Aureon (MRM) — A Cognifin Platform', path: '/solutions/aureon', external: false },
  ];

  const navLinks = [
    { path: '/platform', label: 'Platform' },
    { path: '/use-cases', label: 'Use Cases' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isSolutionsActive = () => location.pathname.startsWith('/solutions');
  const desktopNavClass = (active) =>
    `relative px-4 py-2 text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
      active
        ? 'text-white'
        : 'text-[#D1D5DB] hover:text-white'
    }`;
  const desktopNavIndicatorClass = (active) =>
    `pointer-events-none absolute inset-x-2 -bottom-1 h-px transition-all duration-200 ${
      active ? 'bg-[#93C5FD] opacity-100' : 'bg-transparent opacity-0'
    }`;
  const mobileNavClass = (active) =>
    `px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border ${
      active
        ? 'text-white bg-white/5 border-white/12 shadow-[inset_3px_0_0_0_#93C5FD]'
        : 'text-[#D1D5DB] border-transparent hover:text-white hover:bg-white/6'
    }`;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSolutionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628]/95 backdrop-blur-[12px] border-b border-white/10">
      <nav className="max-w-[1200px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_nav-routing-upgrade/artifacts/decdjpmm_Logo%20H_without%20tagline.png" 
              alt="Cognifin" 
              className="h-8"
            />
          </Link>
          
          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <div className="flex items-center space-x-1">
              {/* Solutions Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setSolutionsOpen(!solutionsOpen)}
                  onMouseEnter={() => setSolutionsOpen(true)}
                  className={desktopNavClass(isSolutionsActive())}
                  data-testid="nav-link-solutions"
                >
                  Solutions
                  <ChevronDown size={16} className={`transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                  <span className={desktopNavIndicatorClass(isSolutionsActive())} />
                </button>
                
                {solutionsOpen && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-72 bg-[#0A1628] border border-white/10 rounded-xl shadow-2xl py-2 z-50"
                    onMouseLeave={() => setSolutionsOpen(false)}
                  >
                    {solutionItems.map((item) => (
                      item.external ? (
                        <a
                          key={item.path}
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between px-4 py-3 text-sm text-[#D1D5DB] hover:text-white hover:bg-white/10 transition-colors"
                          onClick={() => setSolutionsOpen(false)}
                        >
                          <span>{item.label}</span>
                          <ExternalLink size={14} className="text-[#6B7280]" />
                        </a>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-3 text-sm rounded-lg transition-colors ${
                            isActive(item.path)
                              ? 'text-white bg-white/6 border-l border-l-[#93C5FD]'
                              : 'text-[#D1D5DB] hover:text-white hover:bg-white/10'
                          }`}
                          onClick={() => setSolutionsOpen(false)}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Other Nav Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={desktopNavClass(isActive(link.path))}
                >
                  {link.label}
                  <span className={desktopNavIndicatorClass(isActive(link.path))} />
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <div className="flex flex-col space-y-2">
              {/* Mobile Solutions Accordion */}
              <div>
                <button
                  onClick={() => setSolutionsOpen(!solutionsOpen)}
                  className={`w-full flex items-center justify-between ${mobileNavClass(isSolutionsActive())}`}
                >
                  Solutions
                  <ChevronDown size={16} className={`transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {solutionsOpen && (
                  <div className="ml-4 mt-2 space-y-1">
                    {solutionItems.map((item) => (
                      item.external ? (
                        <a
                          key={item.path}
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between px-4 py-2 text-sm text-[#D1D5DB] hover:text-white rounded-lg hover:bg-white/10"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span>{item.label}</span>
                          <ExternalLink size={14} className="text-[#6B7280]" />
                        </a>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-2 text-sm rounded-lg transition-colors ${
                            isActive(item.path)
                              ? 'text-white bg-white/6 border-l border-l-[#93C5FD]'
                              : 'text-[#D1D5DB] hover:text-white hover:bg-white/10'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={mobileNavClass(isActive(link.path))}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
